
import { useState } from "react";
import { ArrowUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReviewCard } from "@/components/ReviewCard";
import { useIsMobile } from "@/hooks/use-mobile";
import type { Review } from "@/types/review";
import { useTranslation } from "@/hooks/useTranslation";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import { Link } from "react-router-dom";

interface ReviewsListProps {
  reviews: Review[];
  onAddReview: () => void;
}

const ReviewsList = ({ reviews, onAddReview }: ReviewsListProps) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { user, isAdmin } = useAdminStatus();
  const [sortAscending, setSortAscending] = useState(false);
  
  // Sort reviews by date and net votes
  const sortedReviews = [...reviews].sort((a, b) => {
    // First sort by date based on sortAscending
    const dateComparison = sortAscending
      ? new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      : new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    
    if (dateComparison !== 0) return dateComparison;
    
    // If dates are equal, sort by net votes
    const aNetVotes = (a.upvotes || 0) - (a.downvotes || 0);
    const bNetVotes = (b.upvotes || 0) - (b.downvotes || 0);
    return bNetVotes - aNetVotes;
  });

  return (
    <section className="mb-8">
      <div className={`flex ${isMobile ? 'flex-col' : 'flex-row justify-between'} items-center mb-4 gap-3`}>
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-semibold">{t('airport_reviews')}</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSortAscending(!sortAscending)}
            className="gap-2"
          >
            <ArrowUpDown className="h-4 w-4" />
            {sortAscending ? t('sort_oldest') : t('sort_newest')}
          </Button>
        </div>
        {user ? (
          <Button onClick={onAddReview} className={isMobile ? "w-full mt-2" : ""}>{t('add_review_button')}</Button>
        ) : (
          <Button asChild className={isMobile ? "w-full mt-2" : ""}>
            <Link to="/auth">{t('sign_in_to_review')}</Link>
          </Button>
        )}
      </div>
      {sortedReviews.length === 0 ? (
        <div className="text-center py-8">
          <h2 className="text-xl font-semibold">{t('no_reviews_yet_share')}</h2>
          <p className="text-muted-foreground mt-2">{t('share_film_community')}</p>
        </div>
      ) : (
        <div className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
          {sortedReviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      )}
    </section>
  );
};

export { ReviewsList };
